import React, { useCallback, useMemo, useState } from 'react'
import { FormProvider, useFormContext, UseFormReturn, UseFormSetError } from 'react-hook-form'
import { useSearchParams } from 'react-router'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { Panel } from '@components/Panel/Panel'
import { Token } from '@components/Tokens/TokenSelector/tokenSchemas'
import { useUSDCMetadata } from '@components/Web3/UserOpTxModal/usdc/qr-fund/useUSDCAddress'
import { ErrorMessage, FancyButton, FormRender, Stack, Text, TextField } from '@ui'
import { invalidateCollectionsForAddressQueryData } from 'api/lib/tokenContracts'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { formatUnits, useBalance } from 'hooks/useBalance'
import { useUSDCBalance } from 'hooks/useUsdcBalance'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import { shortAddress } from 'ui/utils/utils'
import {
    Address,
    BlockchainTransactionType,
    SpaceIdFromSpaceAddress,
    useGetSingleSubscription,
    useIsTransactionPending,
    useMyLinkedWallets,
    useSanctionedAddresses,
    useTransferAssetTransaction,
} from 'use-towns-client'
import { parseUnits as viemParseUnits } from 'viem'

import {
    useEthInputChange,
    useEthInputChangeWithBalanceCheck,
} from '../EditMembership/useEthInputChange'
import { useNavigateToSubscription } from '../Subscriptions/hooks/useNavigateToSubscription'
import { ChainWalletAssets } from '../Trading/tradingUtils'
import { FullPanelOverlay } from '../WalletLinkingPanel'

import { AssetSelector } from './AssetSelector'
import { getEthTransferData, getNftTransferData, getUsdcTransferData } from './transferAssetsData'
import { TransferSchema, transferSchema } from './transferAssetsSchema'
import { TreasuryOrNftConfirmModal } from './TreasuryOrNftConfirmModal'
import { useBaseNftsForTransfer } from './useBaseNftsForTransfer'
import { useGetAssetSourceParam, useIsAAWallet } from './useGetWalletParam'
import { WalletSelector } from './WalletSelector'

export const TransferAssetsPanel = React.memo(() => {
    return <TransferAssets />
})

function TransferAssets() {
    const assetSourceParam = useGetAssetSourceParam()
    const isAAWallet = useIsAAWallet()
    const source = isAAWallet ? assetSourceParam : undefined
    const [searchParams] = useSearchParams()
    const defaultData = searchParams.get('data')
    let defaultAssetToTransfer
    try {
        defaultAssetToTransfer = defaultData
            ? JSON.parse(defaultData).assetAddressOrIdentifier
            : undefined
    } catch (error) {
        console.error('[TransferAssetsPanel] Failed to parse default data:', error)
        defaultAssetToTransfer = undefined
    }
    const isEthTransfer = defaultAssetToTransfer === 'BASE_ETH'

    // USDC detection
    const { address: usdcAddress } = useUSDCMetadata()
    const isUsdcTransfer =
        usdcAddress && defaultAssetToTransfer?.toLowerCase() === usdcAddress.toLowerCase()

    const isPendingTransferBaseEth = useIsTransactionPending(
        BlockchainTransactionType.TransferBaseEth,
    )
    const isPendingTransferNft = useIsTransactionPending(BlockchainTransactionType.TransferNft)

    const isPendingTransferAsset = isPendingTransferBaseEth || isPendingTransferNft

    const { data: aaAddress } = useMyAbstractAccountAddress()

    const { nfts, isFetching: isFetchingNfts } = useBaseNftsForTransfer({
        walletAddress: source,
        enabled: !isEthTransfer && !isUsdcTransfer,
    })
    const { data: linkedWallets, isLoading: isLinkedWalletsLoading } = useMyLinkedWallets()
    const { data: fromBalance } = useBalance({
        address: source,
        enabled: !!source,
    })
    const { data: usdcBalance } = useUSDCBalance({
        address: source,
        enabled: !!source && isUsdcTransfer,
    })

    if (!source) {
        return <></>
    }

    const getPanelTitle = () => {
        if (isEthTransfer) {
            return 'Transfer ETH'
        }
        if (isUsdcTransfer) {
            return 'Transfer USDC'
        }
        return 'Transfer Asset'
    }

    return (
        <Panel label={getPanelTitle()} padding="none" data-testid="transfer-assets-panel">
            {isFetchingNfts || isLinkedWalletsLoading ? (
                <Stack padding="md">
                    <ButtonSpinner />
                </Stack>
            ) : (
                <Stack padding height="100%">
                    <TransferAssetsForm
                        assetSourceParam={assetSourceParam}
                        source={source}
                        isEthTransfer={isEthTransfer}
                        isUsdcTransfer={!!isUsdcTransfer}
                        usdcAddress={usdcAddress}
                        usdcBalance={usdcBalance}
                        fromBalance={fromBalance}
                        nfts={nfts}
                        linkedWallets={linkedWallets}
                        isPendingTransferAsset={isPendingTransferAsset}
                        aaAddress={aaAddress}
                    />
                </Stack>
            )}
        </Panel>
    )
}

export function TransferAssetsForm(props: {
    assetSourceParam: Address | undefined
    source: Address | undefined
    isEthTransfer: boolean
    isUsdcTransfer: boolean
    usdcAddress: Address | undefined
    usdcBalance: ReturnType<typeof useUSDCBalance>['data']
    fromBalance: ReturnType<typeof useBalance>['data']
    nfts?: Token[]
    chainWalletAssets?: ChainWalletAssets[]
    linkedWallets: string[] | undefined
    isPendingTransferAsset: boolean
    aaAddress: Address | undefined
}) {
    const {
        assetSourceParam,
        source,
        isEthTransfer,
        isUsdcTransfer,
        usdcAddress,
        usdcBalance,
        fromBalance,
        nfts,
        linkedWallets,
        isPendingTransferAsset,
        aaAddress,
    } = props
    // Determine default asset identifier
    const getDefaultAsset = () => {
        if (isEthTransfer) {
            return 'BASE_ETH'
        }
        if (isUsdcTransfer && usdcAddress) {
            return usdcAddress
        }
        return undefined
    }

    return (
        <FormRender
            gap
            schema={transferSchema}
            defaultValues={{
                assetAddressOrIdentifier: getDefaultAsset(),
            }}
            id="TransferAssets"
            mode="onChange"
            height="100%"
        >
            {(form: UseFormReturn<TransferSchema>) => {
                const isSubmitting = form.formState.isSubmitting

                return (
                    <FormProvider {...form}>
                        {!isEthTransfer && !isUsdcTransfer && nfts ? (
                            <>
                                <Text strong>Asset</Text>
                                <AssetSelector
                                    nfts={nfts}
                                    fromWallet={assetSourceParam}
                                    fromBalance={fromBalance}
                                />
                            </>
                        ) : null}

                        <EthInputField fromBalance={fromBalance} />
                        <UsdcInputField usdcBalance={usdcBalance} usdcAddress={usdcAddress} />
                        <Stack gap>
                            <Text strong>Send to</Text>
                            <WalletSelector
                                aaAddress={aaAddress}
                                linkedWallets={linkedWallets}
                                fromWallet={source}
                            />
                        </Stack>

                        <SubmitButton
                            source={source}
                            aaAddress={aaAddress}
                            usdcAddress={usdcAddress}
                        />
                        {(isPendingTransferAsset || isSubmitting) && (
                            <FullPanelOverlay withSpinner={false} />
                        )}
                    </FormProvider>
                )
            }}
        </FormRender>
    )
}

export function EthInputField(props: { fromBalance: ReturnType<typeof useBalance>['data'] }) {
    const { fromBalance } = props
    const { register, formState, watch, setValue, trigger, setError } =
        useFormContext<TransferSchema>()
    const assetAddressOrIdentifier = watch('assetAddressOrIdentifier')
    const ethAmount = watch('ethAmount')
    const onCostChange = useEthInputChange(ethAmount ?? '', 'ethAmount', setValue, trigger)

    const onCostChangeWithBalanceCheck = useEthInputChangeWithBalanceCheck({
        balance: fromBalance?.value,
        ethAmount: ethAmount ?? '',
        path: 'ethAmount',
        setValue,
        trigger,
        onError: () => {
            setError('ethAmount', {
                message: 'Insufficient balance',
            })
        },
    })

    const onMaxClick = () => {
        if (fromBalance) {
            // no rounding the value
            onCostChange(formatUnits(fromBalance.value))
        }
    }

    if (assetAddressOrIdentifier !== 'BASE_ETH') {
        return null
    }

    return (
        <Stack gap="sm">
            <TextField
                autoFocus
                data-testid="eth-amount"
                background="level2"
                placeholder="Enter amount"
                after={<Text color="gray2">ETH</Text>}
                renderLabel={(label) => (
                    <Stack horizontal justifyContent="spaceBetween" alignItems="center">
                        <Text strong>{label}</Text>
                        <Stack cursor="pointer" data-testid="max-button" onClick={onMaxClick}>
                            <Text color="cta2">Enter Max</Text>
                        </Stack>
                    </Stack>
                )}
                label="Amount"
                tone={formState.errors.ethAmount ? 'error' : 'neutral'}
                {...register('ethAmount')}
                onChange={onCostChangeWithBalanceCheck}
            />
            <ErrorMessage preventSpace errors={formState.errors} fieldName="ethAmount" />
        </Stack>
    )
}

function UsdcInputField(props: {
    usdcBalance: ReturnType<typeof useUSDCBalance>['data']
    usdcAddress: Address | undefined
}) {
    const { usdcBalance, usdcAddress } = props
    const { register, formState, watch, setValue, setError } = useFormContext<TransferSchema>()
    const assetAddressOrIdentifier = watch('assetAddressOrIdentifier')
    const ethAmount = watch('ethAmount')

    const isUsdcSelected =
        usdcAddress && assetAddressOrIdentifier?.toLowerCase() === usdcAddress.toLowerCase()

    const onMaxClick = () => {
        if (usdcBalance?.formatted) {
            setValue('ethAmount', usdcBalance.formatted, { shouldValidate: true })
        }
    }

    const onAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        // Allow only valid decimal numbers
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setValue('ethAmount', value, { shouldValidate: true })

            // Check balance
            if (usdcBalance?.value && value) {
                try {
                    const inputAmount = viemParseUnits(value || '0', 6)
                    if (inputAmount > usdcBalance.value) {
                        setError('ethAmount', { message: 'Insufficient USDC balance' })
                    }
                } catch {
                    // Invalid input, let form validation handle it
                }
            }
        }
    }

    if (!isUsdcSelected) {
        return null
    }

    return (
        <Stack gap="sm">
            <TextField
                autoFocus
                data-testid="usdc-amount"
                background="level2"
                placeholder="Enter amount"
                after={<Text color="gray2">USDC</Text>}
                renderLabel={(label) => (
                    <Stack horizontal justifyContent="spaceBetween" alignItems="center">
                        <Text strong>{label}</Text>
                        <Stack cursor="pointer" data-testid="max-button" onClick={onMaxClick}>
                            <Text color="cta2">Enter Max</Text>
                        </Stack>
                    </Stack>
                )}
                label="Amount"
                tone={formState.errors.ethAmount ? 'error' : 'neutral'}
                {...register('ethAmount')}
                value={ethAmount ?? ''}
                onChange={onAmountChange}
            />
            <ErrorMessage preventSpace errors={formState.errors} fieldName="ethAmount" />
            <Text size="sm" color="gray2">
                Available: {usdcBalance?.formatted ?? '0'} USDC
            </Text>
        </Stack>
    )
}

function SubmitButton(props: {
    source: Address | undefined
    aaAddress: Address | undefined
    usdcAddress: Address | undefined
}) {
    const { source, aaAddress, usdcAddress } = props
    const { handleSubmit, formState, watch, setError, reset, getValues } =
        useFormContext<TransferSchema>()
    const allValues = watch()
    const { assetAddressOrIdentifier, ethAmount, recipient, tokenId } = allValues
    const { transferAsset } = useTransferAssetTransaction()
    const { isSanctioned } = useSanctionedAddresses()
    const [showTreasuryOrNftConfirm, setShowTreasuryOrNftConfirm] = useState(false)
    const { closePanel } = usePanelActions()

    const { data: subscription, isLoading: isLoadingSubscription } = useGetSingleSubscription({
        walletAddress: aaAddress ?? '',
        spaceAddress: assetAddressOrIdentifier ?? '',
        tokenId: tokenId ?? '',
        enabled: !!aaAddress && !!tokenId && !!assetAddressOrIdentifier,
    })

    const hasActiveSubscription = subscription?.active

    const navigateToSubscription = useNavigateToSubscription()

    const isUsdcTransfer =
        usdcAddress && assetAddressOrIdentifier?.toLowerCase() === usdcAddress.toLowerCase()

    const isDisabled = useMemo(() => {
        if (
            formState.isSubmitting ||
            isLoadingSubscription ||
            !assetAddressOrIdentifier ||
            !recipient
        ) {
            return true
        }
        // For ETH or USDC transfers, require amount
        if (
            (assetAddressOrIdentifier === 'BASE_ETH' || isUsdcTransfer) &&
            (formState.errors.ethAmount || !ethAmount)
        ) {
            return true
        }
        return false
    }, [
        formState.isSubmitting,
        formState.errors.ethAmount,
        assetAddressOrIdentifier,
        ethAmount,
        recipient,
        isLoadingSubscription,
        isUsdcTransfer,
    ])

    const makeTransferTx = useCallback(
        async (dataToSubmit: ReturnType<typeof getValidatedData>, getSigner: GetSigner) => {
            if (!dataToSubmit) {
                return
            }
            // Check if it's an NFT transfer by looking for tokenId in the transfer data
            const _isNftTransfer = 'tokenId' in dataToSubmit && !!dataToSubmit.tokenId
            // Check if it's a USDC/ERC20 transfer by looking for erc20Amount
            const _isErc20Transfer = 'erc20Amount' in dataToSubmit && !!dataToSubmit.erc20Amount
            const signer = await getSigner()
            if (!signer) {
                createPrivyNotAuthenticatedNotification()
                return
            }

            try {
                const context = await transferAsset({
                    data: dataToSubmit,
                    signer,
                })

                if (context?.receipt?.status === 1) {
                    // Success
                    if (_isNftTransfer && source) {
                        await invalidateCollectionsForAddressQueryData(source)
                        reset()
                    } else if (_isErc20Transfer) {
                        const recipientShort = dataToSubmit.recipient
                            ? shortAddress(dataToSubmit.recipient)
                            : 'recipient'
                        popupToast(({ toast }) => (
                            <StandardToast.Success
                                toast={toast}
                                message={`Successfully sent ${dataToSubmit.assetLabel} to ${recipientShort}`}
                            />
                        ))
                        reset()
                        closePanel()
                    }
                } else if (context?.error) {
                    // Transaction failed
                    console.error('Transfer failed:', context.error)
                    popupToast(({ toast }) => (
                        <StandardToast.Error
                            toast={toast}
                            message={`Failed to send ${dataToSubmit.assetLabel ?? 'asset'}`}
                        />
                    ))
                }
            } catch (error) {
                console.error('Transfer error:', error)
                popupToast(({ toast }) => (
                    <StandardToast.Error
                        toast={toast}
                        message={`Failed to send ${dataToSubmit.assetLabel ?? 'asset'}`}
                    />
                ))
            }
        },
        [reset, source, transferAsset, closePanel],
    )

    const onModalSubmit = useCallback(
        async (getSigner: GetSigner) => {
            setShowTreasuryOrNftConfirm(false)
            const validData: TransferSchema = getValues()
            const dataToSubmit = getValidatedData({
                data: validData,
                setError,
                source,
                isSanctioned,
                usdcAddress,
            })
            await makeTransferTx(dataToSubmit, getSigner)
        },
        [getValues, setError, source, makeTransferTx, isSanctioned, usdcAddress],
    )

    const onValid = useCallback(
        async (data: TransferSchema, getSigner: GetSigner) => {
            const _isNftTransfer = isNftTransfer(data, usdcAddress)

            const dataToSubmit = getValidatedData({
                data,
                setError,
                source,
                isSanctioned,
                usdcAddress,
            })

            console.log('TransferAssetsPanel::onValid', dataToSubmit)

            if (!dataToSubmit) {
                return
            }

            if (_isNftTransfer) {
                setShowTreasuryOrNftConfirm(true)
                return
            }

            await makeTransferTx(dataToSubmit, getSigner)
        },
        [setError, source, makeTransferTx, isSanctioned, usdcAddress],
    )

    return (
        <WalletReady>
            {({ getSigner }) => (
                <>
                    <Stack grow />
                    {hasActiveSubscription ? (
                        <Stack centerContent padding="lg">
                            <Text>
                                You have an active subscription.{' '}
                                <Stack
                                    as="button"
                                    type="button"
                                    display="inline"
                                    background="none"
                                    cursor="pointer"
                                    textDecoration="underline"
                                    color="cta2"
                                    onClick={() => {
                                        if (assetAddressOrIdentifier) {
                                            navigateToSubscription(
                                                SpaceIdFromSpaceAddress(assetAddressOrIdentifier),
                                            )
                                        }
                                    }}
                                >
                                    <Text>Please cancel it first.</Text>
                                </Stack>
                            </Text>
                        </Stack>
                    ) : (
                        <FancyButton
                            borderRadius="lg"
                            cta={!isDisabled}
                            data-testid="submit-button"
                            disabled={isDisabled}
                            onClick={handleSubmit((data) => onValid(data, getSigner))}
                        >
                            Send
                        </FancyButton>
                    )}
                    <TreasuryOrNftConfirmModal
                        data={allValues}
                        showModal={showTreasuryOrNftConfirm}
                        type="nft"
                        fromWallet={source}
                        onHide={() => setShowTreasuryOrNftConfirm(false)}
                        onSubmit={() => onModalSubmit(getSigner)}
                    />
                </>
            )}
        </WalletReady>
    )
}

export function getValidatedData(args: {
    data: TransferSchema
    setError: UseFormSetError<TransferSchema>
    source: Address | undefined
    isSanctioned: (address: string) => boolean
    usdcAddress: Address | undefined
}) {
    const { data, setError, source, isSanctioned, usdcAddress } = args

    if (data.recipient && isSanctioned(data.recipient.toLowerCase())) {
        setError('recipient', {
            type: 'manual',
            message: "This wallet has been blocked by OFAC's Sanctions List",
        })
        popupToast(({ toast }) => (
            <StandardToast.Error
                toast={toast}
                message="This wallet has been blocked by OFAC's Sanctions List"
            />
        ))
        return
    }

    const _isUsdcTransfer = isUsdcTransfer(data, usdcAddress)
    const _isNftTransfer = isNftTransfer(data, usdcAddress)

    if (
        !data.assetAddressOrIdentifier ||
        !data.recipient ||
        (!_isNftTransfer && !_isUsdcTransfer && (!data.ethAmount || data.tokenId)) ||
        (_isUsdcTransfer && !data.ethAmount) ||
        (_isNftTransfer && (!data.tokenId || data.ethAmount))
    ) {
        return
    }

    const onParseError = () => {
        setError('ethAmount', {
            type: 'manual',
            message: _isUsdcTransfer
                ? 'Please enter a valid USDC value.'
                : 'Please enter a valid eth value.',
        })
    }

    let dataToSubmit
    if (_isUsdcTransfer && usdcAddress) {
        dataToSubmit = getUsdcTransferData({ data, usdcAddress, onParseError })
    } else if (_isNftTransfer) {
        dataToSubmit = getNftTransferData({ data, fromWallet: source })
    } else {
        dataToSubmit = getEthTransferData({ data, onParseError })
    }

    // Check for positive value (ETH transfers)
    if (dataToSubmit?.value !== undefined && dataToSubmit.value <= 0n) {
        setError('ethAmount', {
            type: 'manual',
            message: 'Please enter a positive number.',
        })
        return
    }

    // Check for positive erc20Amount (USDC transfers)
    if (dataToSubmit?.erc20Amount !== undefined && dataToSubmit.erc20Amount <= 0n) {
        setError('ethAmount', {
            type: 'manual',
            message: 'Please enter a positive number.',
        })
        return
    }

    if (!dataToSubmit) {
        popupToast(({ toast }) => (
            <StandardToast.Error toast={toast} message="There was an error with your submission." />
        ))
        return
    }

    return Object.freeze(dataToSubmit)
}

const isUsdcTransfer = (data: TransferSchema, usdcAddress: Address | undefined) =>
    usdcAddress && data.assetAddressOrIdentifier?.toLowerCase() === usdcAddress.toLowerCase()

const isNftTransfer = (data: TransferSchema, usdcAddress: Address | undefined) =>
    data.assetAddressOrIdentifier !== 'BASE_ETH' && !isUsdcTransfer(data, usdcAddress)
